import React, { CSSProperties } from 'react';

import { isNotNil } from '##/utils/type-guards';

import { FieldPropForm, FieldPropStatus, FieldPropView } from '../types';
import {
  borderRadiusMap,
  borderWidthMap,
  FieldControlLayoutBorderRadiusNode,
  FieldControlLayoutBorderWidthNode,
  FieldControlLayoutPaddingNode,
  paddingMap,
} from './maps';

export const getSlots = (
  side: React.ReactNode | React.ReactNode[],
): React.ReactNode[] => {
  return ((Array.isArray(side) ? side : [side]) as []).filter(
    (item) => isNotNil(item) && item !== false,
  );
};

const borderToCss = (value: FieldControlLayoutBorderWidthNode) => {
  return value ? 'solid' : 'none';
};

export const getBorderStyle = (
  form: FieldPropForm,
  view: FieldPropView,
): string => {
  return view === 'default'
    ? `solid ${borderToCss(borderWidthMap[form][1])} solid ${borderToCss(
        borderWidthMap[form][0],
      )}`
    : `none`;
};

export const getBorderFocusHelper = (
  form: FieldPropForm,
  view: FieldPropView,
) => {
  if (view !== 'default') {
    return;
  }
  if (borderWidthMap[form][0] === false && borderWidthMap[form][1] === false) {
    return 'both';
  }
  if (borderWidthMap[form][0] === false) {
    return 'left';
  }
  if (borderWidthMap[form][1] === false) {
    return 'right';
  }
};

const paddingToCss = (
  value: FieldControlLayoutPaddingNode,
  additional: string = '0px',
): string => {
  return value === 'increased'
    ? `calc(var(--field-control-layout-space) * 1.6 + ${additional})`
    : `calc(var(--field-control-layout-space) + ${additional})`;
};

export const getPaddingVertical = (
  view: FieldPropView,
  additional: string = '0px',
): string => {
  return view === 'default'
    ? additional
    : `calc(var(--control-border-width) + ${additional})`;
};

export const getPaddingTop = (view: FieldPropView): string =>
  `var(--field-control-layout-padding-top-override,${getPaddingVertical(
    view,
    'var(--field-control-layout-additional-padding-top, 0px)',
  )})`;

export const getPaddingBottom = (view: FieldPropView): string =>
  `var(--field-control-layout-padding-bottom-override,
  ${getPaddingVertical(
    view,
    'var(--field-control-layout-additional-padding-bottom, 0px)',
  )})`;

export const getPaddingLeft = (
  view: FieldPropView,
  form: FieldPropForm,
): string => {
  return `var(--field-control-layout-padding-left-override,${
    view === 'default'
      ? paddingToCss(
          paddingMap[form][0],
          'var(--field-control-layout-additional-padding-left, 0px)',
        )
      : `var(--field-control-layout-additional-padding-left, 0px)`
  })`;
};

export const getPaddingRight = (
  view: FieldPropView,
  form: FieldPropForm,
): string => {
  return `var(--field-control-layout-padding-right-override, ${
    view === 'default'
      ? paddingToCss(
          paddingMap[form][1],
          'var(--field-control-layout-additional-padding-right, 0px)',
        )
      : `var(--field-control-layout-additional-padding-right, 0px)`
  })`;
};

const borderRadiusToCss = (
  value: FieldControlLayoutBorderRadiusNode,
): string => {
  if (value === 'default') {
    return 'var(--control-radius)';
  }
  if (value === 'round') {
    return 'calc(var(--field-control-layout-height) / 2)';
  }
  return '0px';
};

export const getBorderRadius = (
  form: FieldPropForm,
  view: FieldPropView,
): string => {
  return view === 'default'
    ? borderRadiusMap[form].map(borderRadiusToCss).join(' ')
    : `0px 0px 0px 0px`;
};

export const getBorderColor = (
  focused?: boolean,
  hovered?: boolean,
  status?: FieldPropStatus,
  disabled?: boolean,
) => {
  if (disabled) {
    return 'transparent';
  }
  if (focused) {
    return 'var(--color-control-bg-border-focus)';
  }
  if (hovered) {
    return 'var(--color-control-bg-border-default-hover)';
  }
  if (status) {
    return `var(--color-bg-${status})`;
  }
  return 'var(--color-control-bg-border-default)';
};

export const getBgColor = (view: FieldPropView, disabled?: boolean) => {
  if (view === 'default' && disabled) {
    return 'var(--color-control-bg-disable)';
  }
  if (view === 'default') {
    return 'var(--color-bg-default)';
  }
  return 'transparent';
};

export const getSlotsWidthStyles = (
  sizes: number[],
  postfix: string,
): CSSProperties => {
  const style: CSSProperties = {};
  for (let index = 0; index < sizes.length; index++) {
    style[
      `--field-control-layout-${postfix}-slot-content-width-${index}` as 'width'
    ] = `${sizes[index]}px`;
  }

  style[`--field-control-layout-${postfix}-slot-contents-width` as 'width'] =
    `${sizes.length ? sizes.reduce((a, b) => a + b) : 0}px`;

  style[`--field-control-layout-${postfix}-slots-length` as 'width'] =
    `${sizes.length}`;

  return style;
};

// ToDo: Удалить после того как удалим из всех компонентов "clearClear"
export const formGuard = (from: FieldPropForm | 'clearClear'): FieldPropForm =>
  from === 'clearClear' ? 'clear' : from;
